/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useEmailSettingsList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing email config list data fetching and state.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { fetchEmailConfigs } from '../api/emailSettingsApi';

export function useEmailSettingsList() {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || 'main';

  const availableColumns = useMemo(
    () => [
      { key: 'name', label: t('emailSettings.fields.name'), visible: true, sortable: true },
      { key: 'host', label: t('emailSettings.fields.host'), visible: true, sortable: true },
      { key: 'port', label: t('emailSettings.fields.port'), visible: true, sortable: true },
      { key: 'authType', label: t('emailSettings.fields.authType'), visible: true, sortable: true },
      { key: 'fromEmail', label: t('emailSettings.fields.fromEmail'), visible: true, sortable: true },
      { key: 'isActive', label: t('emailSettings.fields.isActive'), visible: true, sortable: true },
      { key: 'isDefault', label: t('emailSettings.fields.isDefault'), visible: true, sortable: true },
      { key: 'actions', label: t('common.actions'), visible: true, sortable: false },
    ],
    [t]
  );

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }): Promise<DataGridFetchResult> => {
      const response = await fetchEmailConfigs(params, tenantSlug, user?.id ? Number(user.id) : undefined);
      return {
        data: response.data || [],
        pagination: response.pagination ? {
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.pages
        } : {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      };
    },
    [tenantSlug, user?.id]
  );

  return useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'emailSettings',
  });
}

